import { useEffect, useState } from "react";
import api from "../utils/api";

export default function PositionsAndOrdersCard({ userId }) {
    const [mode, setMode] = useState("paper");

    const [alpacaPositions, setAlpacaPositions] = useState([]);
    const [alpacaOrders, setAlpacaOrders] = useState([]);
    const [alpacaError, setAlpacaError] = useState("");

    const [tradierPositions, setTradierPositions] = useState([]);
    const [tradierOrders, setTradierOrders] = useState([]);
    const [tradierError, setTradierError] = useState("");

    // ---------- keep mode in sync with localStorage.trade_mode ----------
    useEffect(() => {
        const readMode = () => {
            const raw = (localStorage.getItem("trade_mode") || "paper").toLowerCase();
            setMode(raw === "live" ? "live" : "paper");
        };
        readMode();
        window.addEventListener("storage", readMode);
        return () => window.removeEventListener("storage", readMode);
    }, []);

    const safeArray = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) return val;
        return [val];
    };

    // ================== ALPACA ==================

    // initial load
    useEffect(() => {
        if (!userId) return;

        const load = async () => {
            try {
                const posRes = await api.get("/api/brokers/alpaca/positions", {
                    params: { user_id: userId, mode },
                });
                const raw = posRes.data;
                const arr = Array.isArray(raw) ? raw : safeArray(raw);
                setAlpacaPositions(arr);
                setAlpacaError("");
            } catch (e) {
                setAlpacaError("Could not load positions");
                setAlpacaPositions([]);
            }

            try {
                const ordRes = await api.get("/api/brokers/alpaca/orders", {
                    params: { user_id: userId, mode },
                });
                const rawO = ordRes.data;
                const arrO = Array.isArray(rawO) ? rawO : safeArray(rawO);
                setAlpacaOrders(arrO);
            } catch {
                // ignore
            }
        };

        load();
    }, [userId, mode]);

    // poll orders
    useEffect(() => {
        if (!userId) return;
        const fetchOrders = () => {
            api.get("/api/brokers/alpaca/orders", {
                params: { user_id: userId, mode },
            })
                .then((r) => {
                    const raw = r.data;
                    const arr = Array.isArray(raw) ? raw : safeArray(raw);
                    setAlpacaOrders(arr);
                })
                .catch(() => { });
        };
        const t = setInterval(fetchOrders, 5000);
        return () => clearInterval(t);
    }, [userId, mode]);

    const closeAlpacaPosition = async (symbol) => {
        if (!window.confirm(`Close Alpaca position ${symbol}?`)) return;
        try {
            await api.post("/api/trade/close/equity", {
                user_id: userId,
                symbol,
                mode,
            });
            // refresh
            const posRes = await api.get("/api/brokers/alpaca/positions", {
                params: { user_id: userId, mode },
            });
            const raw = posRes.data;
            const arr = Array.isArray(raw) ? raw : safeArray(raw);
            setAlpacaPositions(arr);
        } catch (e) {
            alert(
                e?.response?.data?.error ||
                e?.response?.data?.detail ||
                e?.message ||
                "Close equity position failed."
            );
        }
    };

    // ================== TRADIER ==================

    // initial load
    useEffect(() => {
        if (!userId) return;

        const load = async () => {
            // positions
            try {
                const { data } = await api.get("/api/tradier/positions", {
                    params: { user_id: userId, mode },
                });

                if (data && data.ok === false && data.error) {
                    setTradierError(data.detail || data.error);
                    setTradierPositions([]);
                } else {
                    const arr = safeArray(
                        data.positions || (data.positions && data.positions.position)
                    );
                    setTradierPositions(
                        arr.filter((p) => p && typeof p === "object")
                    );
                    setTradierError("");
                }
            } catch (e) {
                const apiErr = e?.response?.data;
                const msg =
                    apiErr?.detail ||
                    apiErr?.error ||
                    e?.message ||
                    "Could not load Tradier positions.";
                setTradierError(msg);
                setTradierPositions([]);
            }

            // orders
            try {
                const { data } = await api.get("/api/brokers/tradier/orders", {
                    params: { user_id: userId, mode },
                });
                const arrO = safeArray(data.orders || data.order || data);
                setTradierOrders(arrO.filter((o) => o && typeof o === "object"));
            } catch {
                // ignore
            }
        };

        load();
    }, [userId, mode]);

    // poll tradier orders
    useEffect(() => {
        if (!userId) return;
        const fetchOrders = () => {
            api.get("/api/brokers/tradier/orders", {
                params: { user_id: userId, mode },
            })
                .then((resO) => {
                    const dataO = resO.data || {};
                    const arrO = safeArray(dataO.orders || dataO.order || dataO);
                    setTradierOrders(
                        arrO.filter((o) => o && typeof o === "object")
                    );
                })
                .catch(() => { });
        };
        const t = setInterval(fetchOrders, 10000);
        return () => clearInterval(t);
    }, [userId, mode]);

    const closeTradierPosition = async (optionSymbol) => {
        if (!window.confirm(`Close Tradier option ${optionSymbol}?`)) return;
        try {
            await api.post("/api/trade/close/option", {
                user_id: userId,
                option_symbol: optionSymbol,
                mode,
            });
            // refresh positions
            const { data } = await api.get("/api/tradier/positions", {
                params: { user_id: userId, mode },
            });
            const arr = safeArray(
                data.positions || (data.positions && data.positions.position)
            );
            setTradierPositions(arr.filter((p) => p && typeof p === "object"));
        } catch (e) {
            console.error("closeTradierPosition error", e?.response?.data || e);
            alert(
                e?.response?.data?.error ||
                e?.response?.data?.detail ||
                e?.message ||
                "Could not send close order to Tradier."
            );
        }
    };

    // ================== render helpers ==================

    const renderAlpacaPositions = () => {
        if (alpacaPositions.length === 0) {
            return (
                <div className="text-sm text-zinc-400">
                    No open Alpaca positions
                </div>
            );
        }
        return (
            <div className="space-y-2 text-sm text-zinc-200">
                {alpacaPositions.map((p) => (
                    <div
                        key={p.symbol}
                        className="flex justify-between bg-zinc-800 rounded px-3 py-2"
                    >
                        <div>
                            <div className="font-semibold">{p.symbol}</div>
                            <div className="text-xs text-zinc-400">
                                Avg: {p.avg_entry_price} · Qty: {p.qty}
                            </div>
                        </div>

                        <div className="text-right">
                            <div>Current: {p.current_price}</div>
                            <div
                                className={
                                    Number(p.unrealized_pl) >= 0
                                        ? "text-emerald-400 text-xs"
                                        : "text-red-400 text-xs"
                                }
                            >
                                P/L: {p.unrealized_pl} (
                                {p.unrealized_plpc ? `${p.unrealized_plpc}%` : "—"})
                            </div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    closeAlpacaPosition(p.symbol);
                                }}
                                className="mt-1 px-2 py-1 text-xs rounded bg-red-600 hover:bg-red-500 text-white"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderAlpacaOrders = () => {
        if (alpacaOrders.length === 0) {
            return (
                <div className="text-sm text-zinc-400">
                    No recent Alpaca orders
                </div>
            );
        }
        return (
            <div className="space-y-2 text-sm text-zinc-200">
                {alpacaOrders.slice(0, 5).map((o) => (
                    <div
                        key={o.id}
                        className="flex justify-between bg-zinc-800 rounded px-3 py-2"
                    >
                        <div>
                            <div className="font-semibold">
                                {o.symbol} · {o.side}
                            </div>
                            <div className="text-xs text-zinc-400">
                                {o.qty} @{" "}
                                {o.limit_price || o.filled_avg_price || "mkt"}
                            </div>
                        </div>
                        <div className="text-right text-xs text-zinc-400">
                            {o.status}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderTradierPositions = () => {
        if (tradierError) {
            return (
                <div className="text-sm text-red-400">
                    {tradierError}
                </div>
            );
        }
        if (tradierPositions.length === 0) {
            return (
                <div className="text-sm text-zinc-400">
                    No open Tradier positions
                </div>
            );
        }

        return (
            <div className="space-y-2 text-sm text-zinc-200">
                {tradierPositions.map((p, idx) => {
                    const sym = p.symbol || p.underlying_symbol || "?";
                    const qty = p.quantity ?? p.qty ?? p.size ?? "?";
                    const last =
                        p.last ?? p.close_price ?? p.last_price ?? "-";

                    const mv = Number(p.market_value || 0);
                    const cb =
                        Number(p.cost_basis || p.cost_basis_price || 0);
                    let pl = null;
                    let plPct = null;
                    if (cb > 0 && mv) {
                        pl = mv - cb;
                        plPct = (pl / cb) * 100;
                    }

                    const plClass =
                        pl != null && pl >= 0
                            ? "text-emerald-400"
                            : "text-red-400";

                    return (
                        <div
                            key={p.id || sym || idx}
                            className="flex justify-between bg-zinc-800 rounded px-3 py-2"
                        >
                            <div>
                                <div className="font-medium">
                                    {sym}{" "}
                                    {p.description
                                        ? `· ${p.description}`
                                        : ""}
                                </div>
                                <div className="text-xs text-zinc-400">
                                    Qty: {qty} · Cost:{" "}
                                    {cb || p.cost_basis_price || "-"} · Last:{" "}
                                    {last}
                                </div>
                                {pl != null && (
                                    <div
                                        className={`text-xs ${plClass}`}
                                    >
                                        P/L: {pl.toFixed(2)} (
                                        {plPct.toFixed(2)}%)
                                    </div>
                                )}
                            </div>
                            <div className="text-right text-xs text-zinc-400">
                                <div>
                                    {p.open_date ||
                                        p.date_acquired ||
                                        ""}
                                </div>
                                <button
                                    onClick={() =>
                                        closeTradierPosition(sym)
                                    }
                                    className="mt-1 px-2 py-1 rounded bg-red-600 hover:bg-red-500 text-white"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderTradierOrders = () => {
        if (tradierOrders.length === 0) {
            return (
                <div className="text-sm text-zinc-400">
                    No recent Tradier orders
                </div>
            );
        }
        return (
            <div className="space-y-2 text-sm text-zinc-200">
                {tradierOrders.slice(0, 5).map((o, idx) => (
                    <div
                        key={o.id || idx}
                        className="flex justify-between bg-zinc-800 rounded px-3 py-2"
                    >
                        <div>
                            <div className="font-semibold">
                                {(o.symbol || o.underlying) ?? "?"} ·{" "}
                                {(o.side ||
                                    o.transaction_type ||
                                    "")?.toLowerCase()}
                            </div>
                            <div className="text-xs text-zinc-400">
                                {o.quantity || o.qty || ""} @{" "}
                                {o.price ||
                                    o.avg_fill_price ||
                                    "mkt"}
                            </div>
                        </div>
                        <div className="text-right text-xs text-zinc-400">
                            {o.status || o.order_status || ""} <br />
                            {o.create_date ||
                                o.transaction_date ||
                                ""}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // ================== render ==================

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-4">
            <h3 className="text-lg font-semibold">
                Your Positions (Alpaca – {mode})
            </h3>
            {alpacaError && (
                <div className="text-sm text-red-400 mb-1">
                    {alpacaError}
                </div>
            )}
            {renderAlpacaPositions()}

            <h3 className="text-lg font-semibold pt-4 border-t border-zinc-700">
                Alpaca Recent Orders
            </h3>
            {renderAlpacaOrders()}

            <h3 className="text-lg font-semibold pt-4 border-t border-zinc-700">
                Your Positions (Tradier – {mode})
            </h3>
            {renderTradierPositions()}

            <h3 className="text-lg font-semibold pt-4 border-t border-zinc-700">
                Tradier Recent Orders
            </h3>
            {renderTradierOrders()}
        </div>
    );
}
